/**
 * Main ZapSEA API client
 */

import { ImpactResource } from './resources/impact';
import { InfluenceResource } from './resources/influence';
import { JobsResource } from './resources/jobs';
import { FeedbackResource } from './resources/feedback';
import { ZapSEAError, NetworkError } from './errors';
import { ClientConfig, HealthCheckResponse } from './types';
import { 
  validateApiKey, 
  sanitizeUrl, 
  createUserAgent, 
  shouldRetryError, 
  calculateBackoffDelay,
  delay 
} from './utils';

export class ZapSEA {
  private apiKey: string;
  private baseUrl: string;
  private timeout: number;
  private maxRetries: number;
  private userAgent: string;

  public readonly impact: ImpactResource;
  public readonly influence: InfluenceResource;
  public readonly jobs: JobsResource;
  public readonly feedback: FeedbackResource;

  /**
   * ZapSEA API Client
   * 
   * @example
   * ```typescript
   * const client = new ZapSEA({
   *   apiKey: 'pk_live_...',
   *   baseUrl: 'https://api.politycs.ai'
   * });
   * 
   * const job = await client.impact.simulate({
   *   policyDescription: 'AI regulation for financial services',
   *   analysisDepth: 'comprehensive'
   * });
   * ```
   */
  constructor(config: ClientConfig) {
    // Validate configuration
    if (!config.apiKey) {
      throw new Error('API key is required');
    }

    if (!validateApiKey(config.apiKey)) {
      throw new Error('Invalid API key format. API key should start with pk_live_ or pk_test_');
    }

    this.apiKey = config.apiKey;
    this.baseUrl = sanitizeUrl(config.baseUrl || 'https://api.politycs.ai');
    this.timeout = config.timeout || 60000;
    this.maxRetries = Math.max(0, config.maxRetries || 3);
    this.userAgent = createUserAgent();

    // Initialize resource clients
    this.impact = new ImpactResource(this);
    this.influence = new InfluenceResource(this);
    this.jobs = new JobsResource(this);
    this.feedback = new FeedbackResource(this);
  }

  /**
   * Make authenticated HTTP request
   */
  async request<T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const config: RequestInit = {
      method,
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
        'User-Agent': this.userAgent,
        ...options?.headers
      },
      ...options
    };

    if (data && (method === 'POST' || method === 'PUT')) {
      config.body = JSON.stringify(data);
    }

    // Implement retry logic with exponential backoff
    let lastError: Error;
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);
        
        const response = await fetch(url, {
          ...config,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw await ZapSEAError.fromResponse(response);
        }

        // Handle empty responses
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return await response.json();
        } else {
          return {} as T;
        }
      } catch (error) {
        lastError = error as Error;
        
        // Don't retry on the last attempt or if error shouldn't be retried
        if (attempt === this.maxRetries || !shouldRetryError(error)) {
          break;
        }
        
        // Calculate backoff delay
        const backoffDelay = calculateBackoffDelay(attempt);
        await delay(backoffDelay);
      }
    }

    // Wrap network errors
    if (lastError!.name === 'AbortError') {
      throw new NetworkError(`Request timeout after ${this.timeout}ms`, lastError!);
    }

    throw lastError!;
  }

  /**
   * Check API health status
   */
  async healthCheck(): Promise<HealthCheckResponse> {
    return this.request<HealthCheckResponse>('GET', '/v2/health');
  }

  /**
   * Get current API configuration
   */
  getConfig(): Readonly<{
    baseUrl: string;
    timeout: number;
    maxRetries: number;
    userAgent: string;
  }> {
    return {
      baseUrl: this.baseUrl,
      timeout: this.timeout,
      maxRetries: this.maxRetries,
      userAgent: this.userAgent
    };
  }

  /**
   * Update client configuration
   */
  updateConfig(updates: Partial<Omit<ClientConfig, 'apiKey'>>): void {
    if (updates.baseUrl !== undefined) {
      this.baseUrl = sanitizeUrl(updates.baseUrl);
    }
    if (updates.timeout !== undefined) {
      this.timeout = Math.max(1000, updates.timeout); // Minimum 1 second
    }
    if (updates.maxRetries !== undefined) {
      this.maxRetries = Math.max(0, updates.maxRetries);
    }
  }
}
